import {
  OnboardingPlanOptions,
  Price,
  PriceInterval,
  WebActionEvents,
} from '@griply/types';
import React, { Suspense, useCallback, useMemo } from 'react';
import { isMobileDevice } from 'shared/assets/styles/theme/is-mobile-device';
import { useElectron } from 'shared/hooks/use-is-mac';
import { useRedirectToStripeUrlOrStartTrial } from 'shared/hooks/use-redirect-to-stripe-url-or-start-trial';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { QueryParamOptions, useQueryParams } from 'shared/lib/react-router-dom';
import { getDesktopDownload } from 'shared/utils/get-desktop-download';
import { DesktopDownload } from 'shared/views/onboarding/desktop-download';
import { Premium, PremiumLoading } from 'shared/views/onboarding/premium';

import { OnboardingContext } from './onboarding-context';

export type OnboardingProviderProps = {
  children: React.ReactNode;
};

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  const track = useTrackEvents();
  const { isElectron } = useElectron();
  const [
    {
      [QueryParamOptions.OnboardingPremium]: onboardingPremium,
      [QueryParamOptions.OnboardingDesktop]: onboardingDesktop,
      [QueryParamOptions.Premium]: premium,
    },
    setQueryParams,
  ] = useQueryParams([
    QueryParamOptions.OnboardingPremium,
    QueryParamOptions.OnboardingDesktop,
    QueryParamOptions.Purchase,
    QueryParamOptions.Premium,
  ]);

  const desktopDownload = useMemo(
    () => (isElectron || isMobileDevice() ? null : getDesktopDownload()),
    [isElectron],
  );

  const clearOnboarding = useCallback(() => {
    // clear all onboarding query params so the user is not shown the purchase dialog or desktop step again
    setQueryParams(
      {
        [QueryParamOptions.Premium]: premium,
      },
      true,
    );
  }, [premium, setQueryParams]);

  const closeOnboarding = useCallback(() => {
    track(WebActionEvents.OnboardingPremiumSkipSubmit, undefined);

    if (desktopDownload) {
      setQueryParams(
        {
          [QueryParamOptions.Premium]: premium,
          [QueryParamOptions.OnboardingDesktop]: 'true',
        },
        true,
      );
      return;
    }

    clearOnboarding();
  }, [clearOnboarding, desktopDownload, premium, setQueryParams, track]);

  const { redirectOrStartTrial, isLoading } =
    useRedirectToStripeUrlOrStartTrial();
  const purchasePremium = async (price?: Price) => {
    if (!price) {
      return;
    }

    track(WebActionEvents.OnboardingPremiumTrialSubmit, {
      selected_plan:
        price.interval === PriceInterval.Month
          ? OnboardingPlanOptions.Monthly
          : price.interval === PriceInterval.Year
            ? OnboardingPlanOptions.Yearly
            : OnboardingPlanOptions.Lifetime,
    });

    const trialStarted = await redirectOrStartTrial({
      interval: price.interval,
      coupon: price.coupon?.id,
    });

    // explicit check on boolean
    if (trialStarted === true) {
      queueMicrotask(() =>
        setQueryParams(
          {
            [QueryParamOptions.Premium]: 'true',
          },
          true,
        ),
      );
      closeOnboarding();
    }
  };

  const value = useMemo(() => ({}), []);

  if (onboardingPremium) {
    return (
      <OnboardingContext.Provider value={value}>
        <Suspense fallback={<PremiumLoading onSkip={closeOnboarding} />}>
          <Premium
            onSkip={closeOnboarding}
            onSubmit={purchasePremium}
            isLoading={isLoading}
          />
        </Suspense>
      </OnboardingContext.Provider>
    );
  }

  if (onboardingDesktop && desktopDownload) {
    return (
      <OnboardingContext.Provider value={value}>
        <DesktopDownload
          platform={desktopDownload.platform}
          url={desktopDownload.url}
          onDismiss={clearOnboarding}
        />
      </OnboardingContext.Provider>
    );
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
